import React, { useState, useEffect } from "react";
import {
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Collapse,
  Box,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

const HierarchyDropdown = ({
  hierarchyData,
  selectedItems,
  onSelectionChange,
  useWrappedText = true,
}) => {
  const [expandedItems, setExpandedItems] = useState([]);

  useEffect(() => {
    const allIds = [];
    const traverse = (items) => {
      items.forEach((item) => {
        allIds.push(item.ihId);
        if (item.childHierarchy) {
          traverse(item.childHierarchy);
        }
      });
    };
    traverse(hierarchyData);
    setExpandedItems(allIds);
  }, [hierarchyData]);

  const getLocationCodesForApi = (selectedIds) => {
    const locationCodes = new Set();

    const processHierarchy = (items) => {
      items.forEach((item) => {
        if (selectedItems.includes(item.ihId)) {
          locationCodes.add(item.ihLocationCode);
          return;
        }

        if (item.childHierarchy) {
          processHierarchy(item.childHierarchy);
        }
      });
    };

    processHierarchy(hierarchyData);
    return Array.from(locationCodes);
  };

  const calculateDepth = (items) => {
    const depthMap = new Map();

    const traverse = (item, parentId = null) => {
      const depth = parentId ? depthMap.get(parentId) + 1 : 0;
      depthMap.set(item.ihId, depth);

      if (item.childHierarchy) {
        item.childHierarchy.forEach((child) => traverse(child, item.ihId));
      }
    };

    items.forEach((item) => traverse(item));
    return depthMap;
  };

  // Calculate depths once when component mounts or hierarchy changes
  const [depthMap, setDepthMap] = useState(new Map());

  useEffect(() => {
    setDepthMap(calculateDepth(hierarchyData));
  }, [hierarchyData]);

  const HierarchyItem = ({ item }) => {
    const isExpanded = expandedItems.includes(item.ihId);
    const label = item?.levelName;

    const handleToggle = () => {
      setExpandedItems((prev) =>
        isExpanded
          ? prev.filter((id) => id !== item.ihId)
          : [...prev, item.ihId]
      );
    };

    const handleCheckboxChange = (event) => {
      const isChecked = event.target.checked;
      let newSelectedItems = [...selectedItems];

      const getAllChildIds = (item) => {
        let ids = [];
        if (item.childHierarchy) {
          item.childHierarchy.forEach((child) => {
            ids.push(child.ihId);
            ids = [...ids, ...getAllChildIds(child)];
          });
        }
        return ids;
      };

      if (isChecked) {
        const childIds = getAllChildIds(item);
        newSelectedItems = [
          ...new Set([...newSelectedItems, item.ihId, ...childIds]),
        ];
      } else {
        const childIds = getAllChildIds(item);
        newSelectedItems = newSelectedItems.filter(
          (id) => id !== item.ihId && !childIds.includes(id)
        );
      }

      const locationCodesToSend = getLocationCodesForApi(newSelectedItems);

      onSelectionChange({
        selectedIds: newSelectedItems,
        locationCodes: locationCodesToSend,
      });
    };

    return (
      <>
        <ListItem
          sx={{
            display: "flex",
            alignItems: "center",
            // First level gets 10px, subsequent levels add 16px each
            pl:
              depthMap.get(item.ihId) === 0
                ? "5px"
                : `${depthMap.get(item.ihId) * 10 + 5}px`,
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          {item.childHierarchy && (
            <Box
              onClick={handleToggle}
              sx={{
                cursor: "pointer",
                marginRight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          )}
          
          {useWrappedText ? ( // Conditionally render FormControlLabel based on the prop
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedItems.includes(item.ihId)}
                  onChange={handleCheckboxChange}
                  sx={{
                    padding: 0,
                    "&.MuiCheckbox-root": {
                      padding: "1px",
                    },
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-all",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    fontFamily: "Arial, sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#000000DE",
                  }}
                >
                  {label}
                </Box>
              }
              sx={{
                marginLeft: item.childHierarchy ? 0 : 2,
              }}
            />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedItems.includes(item.ihId)}
                  onChange={handleCheckboxChange}
                  sx={{
                    padding: 0, // Remove checkbox padding
                    "&.MuiCheckbox-root": {
                      padding: "4px",
                    },
                  }}
                />
              }
              label={label}
              sx={{
                marginLeft: item.childHierarchy ? 0 : 4,
                "& .MuiFormControlLabel-label": {
                  color: "#000000DE",
                  fontFamily: "Arial, sans-serif",
                  fontWeight: 400,
                },
              }}
            />
          )}
        </ListItem>
        {item.childHierarchy && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.childHierarchy.map((child) => (
                <HierarchyItem key={child.ihId} item={child} />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
      {hierarchyData.map((item) => (
        <HierarchyItem key={item.ihId} item={item} />
      ))}
    </List>
  );
};

export default HierarchyDropdown;
